import { client } from '../..'
import {
  CREATE_ATTENTION_POINT,
  CREATE_KUDOS,
  DELETE_ATTENTION_POINT,
  DELETE_KUDOS,
  DELETE_KUDOS_COMPANY_COUNTRY_VISIBILITY,
  CREATE_OR_EDIT_KUDOS_COMPANY_COUNTRY_VISIBILITY,
  EDIT_KUDOS_LEGAL_ENTITY_VISIBILITY,
  UPDATE_EMPLOYEE_OPT_OUT_VALUE,
  UPDATE_MY_OPT_OUT_VALUE,
} from './Mutations'
import {
  GET_KUDOS_FOR_EMPLOYEE_IN_APPRAISAL,
  GET_ALL_KUDOS,
  GET_KUDOS_GIVEN_BY_REPLYER,
  GET_KUDOS_FOR_EMPLOYEE,
  GET_ALL_ATTENTION_POINTS,
  GET_ATTENTION_POINTS_FOR_EMPLOYEE,
  GET_KUDOS_RECEIVED_BY_ME,
  ARE_KUDOS_VISIBLE_FOR_ME,
  GET_KUDOS_LEGAL_ENTITY_VISIBILITY_DATA,
  GET_KUDOS_COMPANY_COUNTRY_VISIBILITY_DATA,
} from './Queries'
import {
  CreateAttentionPointMutation,
  CreateAttentionPointMutationVariables,
  CreateKudosMutation,
  CreateKudosMutationVariables,
  DeleteAttentionPointMutation,
  DeleteAttentionPointMutationVariables,
  DeleteKudosCompanyCountryVisibilityMutation,
  DeleteKudosCompanyCountryVisibilityMutationVariables,
  DeleteKudosMutation,
  DeleteKudosMutationVariables,
  KudosCompanyCountryVisibilityFilterInput,
  KudosCompanyCountryVisibilityInput,
  KudosFilterInput,
  KudosLegalEntityVisibilityFilterInput,
  KudosLegalEntityVisibilityInput,
  SetKudosCompanyCountryVisibilityMutation,
  SetKudosCompanyCountryVisibilityMutationVariables,
  SetKudosLegalEntityVisibilityMutation,
  SetKudosLegalEntityVisibilityMutationVariables,
  UpdateEmployeeOptOutValueMutation,
  UpdateEmployeeOptOutValueMutationVariables,
  UpdateMyOptOutValueMutation,
  UpdateMyOptOutValueMutationVariables,
} from '../../types'
import { FetchResult } from '@apollo/client'
import { KUDOS_CAROUSEL_PAGE_SIZE } from './util'

export const createKudosInPublicProfile = async (employeeId: string, description: string): Promise<FetchResult<CreateKudosMutation>> => {
  const mutationResult = await client.mutate<CreateKudosMutation, CreateKudosMutationVariables>({
    mutation: CREATE_KUDOS,
    variables: {
      employeeId,
      description,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_FOR_EMPLOYEE,
        variables: {
          employeeId,
          pagination: { page: 0, limit: KUDOS_CAROUSEL_PAGE_SIZE },
          employeeResigned: false,
        },
      },
    ],
  })

  return mutationResult
}

export const createAttentionPointInPublicProfile = async (
  employeeId: string,
  description: string,
): Promise<FetchResult<CreateAttentionPointMutation>> => {
  const mutationResult = await client.mutate<CreateAttentionPointMutation, CreateAttentionPointMutationVariables>({
    mutation: CREATE_ATTENTION_POINT,
    variables: {
      employeeId,
      description,
    },
    refetchQueries: [
      {
        query: GET_ATTENTION_POINTS_FOR_EMPLOYEE,
        variables: {
          employeeId,
          pagination: { page: 0, limit: KUDOS_CAROUSEL_PAGE_SIZE },
        },
      },
    ],
  })

  return mutationResult
}

export const createKudosInMeeting = async (
  employeeId: string,
  description: string,
  meetingType: string,
  appraisalSeasonYear: number,
): Promise<FetchResult<CreateKudosMutation>> => {
  const mutationResult = await client.mutate<CreateKudosMutation, CreateKudosMutationVariables>({
    mutation: CREATE_KUDOS,
    variables: {
      employeeId,
      description,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_FOR_EMPLOYEE_IN_APPRAISAL,
        variables: {
          employeeId,
          appraisalInput: {
            pagination: { page: 0, limit: KUDOS_CAROUSEL_PAGE_SIZE },
            meetingType,
            appraisalSeasonYear,
          },
        },
      },
    ],
  })

  return mutationResult
}

export const createKudos = async (
  employeeId: string,
  description: string,
  filters: KudosFilterInput,
  replyerView: boolean,
): Promise<FetchResult<CreateKudosMutation>> => {
  const mutationResult = await client.mutate<CreateKudosMutation, CreateKudosMutationVariables>({
    mutation: CREATE_KUDOS,
    variables: {
      employeeId,
      description,
    },
  })

  // Refetch queries to update the UI only if mutation was successful
  if (mutationResult.data && !mutationResult.errors) {
    await Promise.all([
      !replyerView
        ? client.query({
            query: GET_ALL_KUDOS,
            variables: {
              filters,
            },
            fetchPolicy: 'network-only',
          })
        : client.query({
            query: GET_KUDOS_GIVEN_BY_REPLYER,
            variables: {
              filters,
            },
            fetchPolicy: 'network-only',
          }),
    ])
  }

  return mutationResult
}

export const createAttentionPoint = async (
  employeeId: string,
  description: string,
  filters: KudosFilterInput,
): Promise<FetchResult<CreateAttentionPointMutation>> => {
  const mutationResult = await client.mutate<CreateAttentionPointMutation, CreateAttentionPointMutationVariables>({
    mutation: CREATE_ATTENTION_POINT,
    variables: {
      employeeId,
      description,
    },
  })

  // Refetch queries to update the UI only if mutation was successful
  if (mutationResult.data && !mutationResult.errors) {
    await Promise.all([
      client.query({
        query: GET_ALL_ATTENTION_POINTS,
        variables: {
          filters,
        },
        fetchPolicy: 'network-only',
      }),
    ])
  }

  return mutationResult
}

export const deleteKudos = async (
  kudosId: string,
  filters: KudosFilterInput,
  replyerView: boolean,
): Promise<FetchResult<DeleteKudosMutation>> => {
  const mutationResult = await client.mutate<DeleteKudosMutation, DeleteKudosMutationVariables>({
    mutation: DELETE_KUDOS,
    variables: {
      kudosId,
    },
  })

  // Refetch queries to update the UI only if mutation was successful
  if (mutationResult.data && !mutationResult.errors) {
    await Promise.all([
      !replyerView
        ? client.query({
            query: GET_ALL_KUDOS,
            variables: {
              filters,
            },
            fetchPolicy: 'network-only',
          })
        : client.query({
            query: GET_KUDOS_GIVEN_BY_REPLYER,
            variables: {
              filters,
            },
            fetchPolicy: 'network-only',
          }),
    ])
  }

  return mutationResult
}

export const deleteAttentionPoint = async (
  attentionPointId: string,
  filters: KudosFilterInput,
): Promise<FetchResult<DeleteAttentionPointMutation>> => {
  const mutationResult = await client.mutate<DeleteAttentionPointMutation, DeleteAttentionPointMutationVariables>({
    mutation: DELETE_ATTENTION_POINT,
    variables: {
      attentionPointId,
    },
  })

  // Refetch queries to update the UI only if mutation was successful
  if (mutationResult.data && !mutationResult.errors) {
    await Promise.all([
      client.query({
        query: GET_ALL_ATTENTION_POINTS,
        variables: {
          filters,
        },
        fetchPolicy: 'network-only',
      }),
    ])
  }

  return mutationResult
}

export const updateMyOptOutValue = async (newOptOutValue: boolean): Promise<FetchResult<UpdateMyOptOutValueMutation>> => {
  const mutationResult = await client.mutate<UpdateMyOptOutValueMutation, UpdateMyOptOutValueMutationVariables>({
    mutation: UPDATE_MY_OPT_OUT_VALUE,
    variables: {
      newOptOutValue,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_RECEIVED_BY_ME,
        variables: {
          pagination: { page: 0, limit: KUDOS_CAROUSEL_PAGE_SIZE },
        },
      },
      {
        query: ARE_KUDOS_VISIBLE_FOR_ME,
      },
    ],
  })

  return mutationResult
}

export const updateEmployeeOptOutValue = async (
  employeeId: string,
  newOptOutValue: boolean,
): Promise<FetchResult<UpdateEmployeeOptOutValueMutation>> => {
  const mutationResult = await client.mutate<UpdateEmployeeOptOutValueMutation, UpdateEmployeeOptOutValueMutationVariables>({
    mutation: UPDATE_EMPLOYEE_OPT_OUT_VALUE,
    variables: {
      employeeId,
      newOptOutValue,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_FOR_EMPLOYEE,
        variables: {
          employeeId,
          pagination: { page: 0, limit: KUDOS_CAROUSEL_PAGE_SIZE },
          employeeResigned: false,
        },
      },
    ],
  })

  return mutationResult
}

// ////////
// SETTINGS
// ////////

export const editKudosLegalEntityVisibility = async (
  kudosLegalEntityVisibilityInput: KudosLegalEntityVisibilityInput,
  filters: KudosLegalEntityVisibilityFilterInput,
): Promise<FetchResult<SetKudosLegalEntityVisibilityMutation>> => {
  const mutationResult = await client.mutate<SetKudosLegalEntityVisibilityMutation, SetKudosLegalEntityVisibilityMutationVariables>({
    mutation: EDIT_KUDOS_LEGAL_ENTITY_VISIBILITY,
    variables: {
      kudosLegalEntityVisibilityInput,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_LEGAL_ENTITY_VISIBILITY_DATA,
        variables: {
          filters,
        },
      },
      {
        query: ARE_KUDOS_VISIBLE_FOR_ME,
      },
    ],
    awaitRefetchQueries: true,
  })

  // Refetch GET_ALL_KUDOS for all active instances to update the dashboard tab
  if (mutationResult.data && !mutationResult.errors) {
    await client.refetchQueries({
      include: [GET_ALL_KUDOS],
    })
  }

  return mutationResult
}

export const createOrEditKudosCompanyCountryVisibility = async (
  kudosCompanyCountryVisibilityInput: KudosCompanyCountryVisibilityInput,
  filters: KudosCompanyCountryVisibilityFilterInput,
): Promise<FetchResult<SetKudosCompanyCountryVisibilityMutation>> => {
  const mutationResult = await client.mutate<SetKudosCompanyCountryVisibilityMutation, SetKudosCompanyCountryVisibilityMutationVariables>({
    mutation: CREATE_OR_EDIT_KUDOS_COMPANY_COUNTRY_VISIBILITY,
    variables: {
      kudosCompanyCountryVisibilityInput,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_COMPANY_COUNTRY_VISIBILITY_DATA,
        variables: {
          filters,
        },
      },
      {
        query: ARE_KUDOS_VISIBLE_FOR_ME,
      },
    ],
    awaitRefetchQueries: true,
  })

  // Refetch GET_ALL_KUDOS for all active instances to update the dashboard tab
  if (mutationResult.data && !mutationResult.errors) {
    await client.refetchQueries({
      include: [GET_ALL_KUDOS],
    })
  }

  return mutationResult
}

export const deleteKudosCompanyCountryVisibility = async (
  kudosCompanyCountryVisibilityId: string,
  filters: KudosCompanyCountryVisibilityFilterInput,
): Promise<FetchResult<DeleteKudosCompanyCountryVisibilityMutation>> => {
  const mutationResult = await client.mutate<
    DeleteKudosCompanyCountryVisibilityMutation,
    DeleteKudosCompanyCountryVisibilityMutationVariables
  >({
    mutation: DELETE_KUDOS_COMPANY_COUNTRY_VISIBILITY,
    variables: {
      kudosCompanyCountryVisibilityId,
    },
    refetchQueries: [
      {
        query: GET_KUDOS_COMPANY_COUNTRY_VISIBILITY_DATA,
        variables: {
          filters,
        },
      },
      {
        query: ARE_KUDOS_VISIBLE_FOR_ME,
      },
    ],
    awaitRefetchQueries: true,
  })

  // Refetch GET_ALL_KUDOS for all active instances to update the dashboard tab
  if (mutationResult.data && !mutationResult.errors) {
    await client.refetchQueries({
      include: [GET_ALL_KUDOS],
    })
  }

  return mutationResult
}
